import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  FileText,
  Users,
  Vault,
  Settings,
  MoreHorizontal,
  FileSignature,
  X,
  Bell,
} from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  DrawerClose,
} from '@/components/ui/drawer';
import { SoloLogoIcon } from '@/components/ui/solo-logo';
import { useState } from 'react';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationsModal } from '@/components/modals/NotificationsModal';
import { ReceiptDivider } from '@/components/ui/receipt-divider';

// Left side nav items (before FAB)
const leftNav = [
  { name: 'Home', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Invoices', href: '/invoices', icon: FileText },
];

// Right side nav items (after FAB)
const rightNav = [
  { name: 'Clients', href: '/clients', icon: Users },
];

const moreNav = [
  { name: 'Tax Vault', href: '/tax-vault', icon: Vault },
  { name: 'Contracts', href: '/contracts', icon: FileSignature },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export function MobileNav() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { unreadCount } = useNotifications();

  const isActive = (href: string) =>
    location.pathname === href ||
    (href !== '/dashboard' && location.pathname.startsWith(href));

  const isMoreActive = moreNav.some((item) => isActive(item.href));
  const isSoloActive = isActive('/solo');

  const NavItem = ({ item }: { item: { name: string; href: string; icon: React.ElementType } }) => {
    const active = isActive(item.href);
    return (
      <Link
        to={item.href}
        className={cn(
          'flex flex-col items-center gap-1 py-2 transition-all flex-1',
          active
            ? 'text-primary'
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        <div
          className={cn(
            'w-10 h-10 flex items-center justify-center transition-all',
            active && 'gradient-gold shadow-glow-sm'
          )}
        >
          <item.icon
            className={cn(
              'w-5 h-5',
              active && 'text-primary-foreground'
            )}
          />
        </div>
        <span className="font-mono text-[10px] font-bold uppercase tracking-wider">{item.name}</span>
      </Link>
    );
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
      {/* Thermal paper background */}
      <div className="absolute inset-0 bg-receipt/95 backdrop-blur-xl border-t-2 border-foreground/10" />
      <div className="absolute inset-0 thermal-paper dark:thermal-paper-dark opacity-30" />
      
      {/* Navigation grid: 5 equal columns */}
      <div className="relative flex items-center justify-around px-2 py-2 safe-area-pb">
        {/* Left side items */}
        {leftNav.map((item) => (
          <NavItem key={item.name} item={item} />
        ))}
        
        {/* Center SOLO FAB - inline with other nav items */}
        <Link
          to="/solo"
          className={cn(
            'flex flex-col items-center gap-1 py-2 transition-all flex-1',
            isSoloActive
              ? 'text-primary'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <div
            className={cn(
              'w-10 h-10 flex items-center justify-center bg-foreground/90 dark:bg-foreground/80 transition-all',
              isSoloActive && 'shadow-glow-sm ring-1 ring-primary'
            )}
          >
            <SoloLogoIcon 
              size="xs" 
              variant="light"
            />
          </div>
          <span className="font-mono text-[10px] font-bold uppercase tracking-wider">Solo</span>
        </Link>

        {/* Right side items */}
        {rightNav.map((item) => (
          <NavItem key={item.name} item={item} />
        ))}

        {/* More Menu - Drawer */}
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerTrigger asChild>
            <button
              className={cn(
                'flex flex-col items-center gap-1 py-2 transition-all flex-1',
                isMoreActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <div
                className={cn(
                  'w-10 h-10 flex items-center justify-center transition-all',
                  isMoreActive && 'gradient-gold shadow-glow-sm'
                )}
              >
                <MoreHorizontal
                  className={cn('w-5 h-5', isMoreActive && 'text-primary-foreground')}
                />
              </div>
              <span className="font-mono text-[10px] font-bold uppercase tracking-wider">More</span>
            </button>
          </DrawerTrigger>
          <DrawerContent className="bg-receipt border-t-2 border-foreground/10">
            <div className="absolute inset-0 thermal-paper dark:thermal-paper-dark opacity-30 pointer-events-none" />
            <DrawerHeader className="relative border-b-2 border-foreground/10 pb-4">
              <div className="flex items-center justify-between">
                <DrawerTitle className="font-mono text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  More Options
                </DrawerTitle>
                <DrawerClose asChild>
                  <button className="p-2 hover:bg-secondary transition-colors">
                    <X className="w-5 h-5 text-muted-foreground" />
                  </button>
                </DrawerClose>
              </div>
            </DrawerHeader>
            <div className="relative p-4 space-y-2">
              {moreNav.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setOpen(false)}
                    className={cn(
                      'flex items-center gap-4 p-4 transition-all border-l-2',
                      active 
                        ? 'bg-primary/10 text-primary border-primary' 
                        : 'bg-secondary/30 hover:bg-secondary border-transparent'
                    )}
                  >
                    <div className={cn(
                      'w-12 h-12 flex items-center justify-center',
                      active ? 'gradient-gold' : 'bg-muted'
                    )}>
                      <item.icon className={cn(
                        'w-6 h-6',
                        active ? 'text-primary-foreground' : 'text-muted-foreground'
                      )} />
                    </div>
                    <div>
                      <p className="font-mono text-sm font-bold uppercase tracking-wider">{item.name}</p>
                      <p className="font-mono text-xs text-muted-foreground">
                        {item.name === 'Tax Vault' && 'Manage your tax savings'}
                        {item.name === 'Contracts' && 'View and manage contracts'}
                        {item.name === 'Settings' && 'Account preferences'}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
            
            {/* Notifications Button */}
            <div className="relative p-4 pt-0">
              <button
                onClick={() => {
                  setOpen(false);
                  setShowNotifications(true);
                }}
                className="w-full flex items-center gap-4 p-4 bg-secondary/30 hover:bg-secondary transition-colors border-l-2 border-transparent"
              >
                <div className="w-12 h-12 bg-primary/10 flex items-center justify-center relative">
                  <Bell className="w-5 h-5 text-primary" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center text-[10px] font-mono font-bold gradient-gold text-primary-foreground px-1">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </div>
                <div className="text-left">
                  <p className="font-mono text-sm font-bold uppercase tracking-wider">Alerts</p>
                  <p className="font-mono text-xs text-muted-foreground">
                    {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                  </p>
                </div>
              </button>
            </div>

            <ReceiptDivider />

            <div className="relative p-4 pt-0">
              <div className="text-center py-4">
                <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
                  Solo Financial Terminal
                </p>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      </div>

      {/* Notifications Modal */}
      <NotificationsModal
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </nav>
  );
}
